/**
 * Operational exception queue (OS Buddy — issue #1097, epic #1085).
 *
 * The shared foreman view of open operational exceptions (capacity_overbooked and
 * the rest of the #1095 domain). Read-only in PR1: it lists active cases, keeps them
 * live over Socket.IO, and lets the foreman narrow to a single urgency. Claim/resolve
 * actions and the OS Buddy hand-off arrive in later PRs.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import { useCollectionSync } from '@/hooks/useEntitySync';
import { queryKeys } from '@/hooks/queryKeys';
import { isActiveException, useExceptions, type ExceptionUrgency } from '@/hooks/useExceptions';
import { ExceptionCard } from './ExceptionCard';
import { ExceptionDetailDialog } from './ExceptionDetailDialog';

type UrgencyFilter = ExceptionUrgency | 'all';

const URGENCY_FILTERS: UrgencyFilter[] = ['all', 'critical', 'high', 'normal', 'low'];

/** Order active cases most-urgent first, then oldest first within an urgency. */
const URGENCY_RANK: Record<ExceptionUrgency, number> = { critical: 0, high: 1, normal: 2, low: 3 };

export function ExceptionsQueue() {
  const { t } = useTranslation('exceptions');
  const { t: tc } = useTranslation('common');
  const { data, isLoading, isError, refetch } = useExceptions();
  const [urgency, setUrgency] = useState<UrgencyFilter>('all');
  const [openId, setOpenId] = useState<string | null>(null);

  // Keep the queue live: any operational_exceptions mutation (#1095/#1096 emit
  // entity:changed) re-fetches the shared list so every foreman sees the same state.
  useCollectionSync('operational_exceptions', {
    invalidationKeys: [{ queryKey: queryKeys.exceptions.lists() }],
  });

  const active = useMemo(() => {
    const list = (data?.exceptions ?? []).filter(isActiveException);
    const byUrgency = urgency === 'all' ? list : list.filter((e) => e.urgency === urgency);
    return [...byUrgency].sort(
      (a, b) =>
        URGENCY_RANK[a.urgency] - URGENCY_RANK[b.urgency] ||
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );
  }, [data, urgency]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => void refetch()}>
            {tc('errors.refresh')}
          </Button>
        }
      >
        {t('error')}
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <ToggleButtonGroup
          value={urgency}
          exclusive
          size="small"
          onChange={(_e, value: UrgencyFilter | null) => value && setUrgency(value)}
          aria-label={t('filter.urgency')}
        >
          {URGENCY_FILTERS.map((value) => (
            <ToggleButton key={value} value={value}>
              {value === 'all' ? t('filter.all') : t(`urgency.${value}`)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Typography variant="body2" color="text.secondary">
          {t('count', { count: active.length })}
        </Typography>
      </Box>

      {active.length === 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 6, textAlign: 'center' }}>
          <InboxOutlinedIcon color="disabled" sx={{ fontSize: 40 }} />
          <Typography variant="body2" color="text.secondary">
            {t('empty')}
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1.5}>
          {active.map((exception) => (
            <ExceptionCard key={exception.id} exception={exception} onOpen={() => setOpenId(exception.id)} />
          ))}
        </Stack>
      )}

      <ExceptionDetailDialog exceptionId={openId} onClose={() => setOpenId(null)} />
    </Stack>
  );
}
